import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Grid,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import { communityAPI } from '../services/api';
import type { CommunityProfile } from '../types';
import '../styles/community.css';

const CommunityDetail: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<CommunityProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        if (!id) {
          setError('Missing profile id');
          return;
        }
        const res = await communityAPI.getPublicById(id);
        setProfile(res.data);
      } catch (e: any) {
        setError(e.response?.data?.message || 'Failed to load profile');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  if (loading) {
    return (
      <Box className="community-profile__loading" sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container className="community-profile__error" maxWidth="md" sx={{ py: { xs: 12, md: 14 } }}>
        <Alert className="community-profile__error-alert" severity="error">{error}</Alert>
        <Button className="community-profile__error-back" sx={{ mt: 2 }} variant="outlined" onClick={() => navigate('/community')}>
          Back to Community
        </Button>
      </Container>
    );
  }

  if (!profile) {
    return (
      <Container className="community-profile__not-found" maxWidth="md" sx={{ py: { xs: 12, md: 14 } }}>
        <Alert className="community-profile__not-found-alert" severity="info">Profile not found</Alert>
        <Button className="community-profile__not-found-back" sx={{ mt: 2 }} variant="outlined" onClick={() => navigate('/community')}>
          Back to Community
        </Button>
      </Container>
    );
  }

  const location = [profile.location?.city, profile.location?.country].filter(Boolean).join(', ');
  const hasSidebar = Boolean(location) || Boolean(profile.websiteUrl) || (Array.isArray(profile.services) && profile.services.length > 0);
  const messageHref = profile.contactEmail ? `mailto:${profile.contactEmail}` : '';

  return (
    <Box className="community-profile" sx={{ bgcolor: '#fafafa', minHeight: '100vh' }}>
      <Box
        className="community-profile__hero"
        sx={{
          background: 'linear-gradient(110deg, rgba(116, 141, 255, 0.45) 0%, rgba(255, 225, 210, 0.35) 55%, rgba(255, 255, 255, 0.7) 100%)',
          pt: { xs: 12, md: 14 },
          pb: { xs: 4, md: 5 },
          minHeight: 400,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-end',
        }}
      >
        <Container className="community-profile__hero-container" maxWidth="lg">
          <Box
            className="community-profile__hero-content"
            sx={{
              mt: 0,
              display: 'flex',
              alignItems: { xs: 'flex-start', md: 'stretch' },
              justifyContent: 'space-between',
              gap: { xs: 2.5, md: 3 },
              flexDirection: { xs: 'column', md: 'row' },
            }}
          >
            <Box className="community-profile__hero-left" sx={{ display: 'flex', alignItems: 'center', gap: 2.5, minWidth: 0 }}>
              {profile.logoDataUrl ? (
                <Box
                  component="img"
                  src={profile.logoDataUrl}
                  alt={profile.displayName}
                  className="community-profile__logo"
                  sx={{
                    width: { xs: 76, md: 96 },
                    height: { xs: 76, md: 96 },
                    borderRadius: 2.5,
                    objectFit: 'cover',
                    flexShrink: 0,
                    bgcolor: '#fff',
                    border: '1px solid',
                    borderColor: 'rgba(0,0,0,0.08)',
                  }}
                />
              ) : null}
              <Box className="community-profile__hero-text" sx={{ minWidth: 0 }}>
                <Typography
                  className="community-profile__display-name"
                  variant="h2"
                  component="h1"
                  sx={{
                    fontWeight: 900,
                    letterSpacing: -0.8,
                    lineHeight: 1.05,
                    fontSize: { xs: 42, sm: 56, md: 64 },
                  }}
                >
                  {profile.displayName}
                </Typography>
                <Typography
                  className="community-profile__summary"
                  variant="body1"
                  color="text.secondary"
                  sx={{ mt: 1, maxWidth: 820, fontWeight: 600 }}
                >
                  {profile.summary || ' '}
                </Typography>
              </Box>
            </Box>

            <Box
              className="community-profile__hero-right"
              sx={{
                alignSelf: { xs: 'stretch', md: 'stretch' },
                display: 'flex',
                flexDirection: 'column',
                justifyContent: { xs: 'flex-start', md: 'flex-end' },
                alignItems: { xs: 'stretch', md: 'flex-end' },
              }}
            >
              <Button
                className="community-profile__cta"
                variant="contained"
                disabled={!profile.contactEmail}
                href={messageHref || undefined}
                sx={{
                  textTransform: 'none',
                  px: 3,
                  py: 1.1,
                  borderRadius: '4px',
                  fontWeight: 700,
                  width: { xs: '100%', md: 'auto' },
                }}
              >
                Send a message
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>

      <Container className="community-profile__body" maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
        <Box
          className="community-profile__grid"
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: hasSidebar ? '1.6fr 1fr' : '1fr' },
            gap: { xs: 3, md: 4 },
            alignItems: 'start',
          }}
        >
          <Box className="community-profile__main" sx={{ pr: { md: 2 }, pb: { xs: 4, md: 6 } }}>
            {profile.summary && (
              <Typography
                className="community-profile__about-heading"
                variant="h4"
                sx={{
                  fontWeight: 900,
                  letterSpacing: -0.4,
                  mb: 2,
                  fontSize: { xs: 26, sm: 32, md: 36 },
                }}
              >
                {profile.summary}
              </Typography>
            )}

            {profile.about ? (
              <Typography
                className="community-profile__about"
                variant="body1"
                sx={{ color: 'text.secondary', lineHeight: 1.75, whiteSpace: 'pre-wrap' }}
              >
                {profile.about}
              </Typography>
            ) : (
              <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.75 }}>
                No additional information has been added yet.
              </Typography>
            )}

            <Divider className="community-profile__divider" sx={{ mt: 4, display: { xs: 'block', md: 'none' } }} />
          </Box>

          {hasSidebar && (
            <Box
              className="community-profile__sidebar"
              sx={{
                bgcolor: '#fff',
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'rgba(0,0,0,0.08)',
                p: { xs: 2.5, md: 3 },
                boxShadow: '0 1px 2px rgba(15, 26, 58, 0.06)',
              }}
            >
              {location && (
                <Box className="community-profile__sidebar-section community-profile__sidebar-section--location" sx={{ mb: 2.5 }}>
                  <Typography className="community-profile__sidebar-label" variant="caption" sx={{ fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase' }}>
                    Location
                  </Typography>
                  <Typography className="community-profile__sidebar-value" variant="body1" sx={{ fontWeight: 700, mt: 0.75 }}>
                    {location}
                  </Typography>
                </Box>
              )}

              {Array.isArray(profile.services) && profile.services.length > 0 && (
                <Box className="community-profile__sidebar-section community-profile__sidebar-section--services" sx={{ mb: 2.5 }}>
                  <Typography className="community-profile__sidebar-label" variant="caption" sx={{ fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase' }}>
                    Services
                  </Typography>
                  <Stack className="community-profile__services" direction="row" spacing={1} sx={{ mt: 1.25, flexWrap: 'wrap' }}>
                    {profile.services.map((s) => (
                      <Chip
                        className="community-profile__service"
                        key={s}
                        label={s}
                        sx={{
                          mb: 1,
                          bgcolor: 'rgba(116, 141, 255, 0.14)',
                          border: '1px solid rgba(116, 141, 255, 0.18)',
                          fontWeight: 600,
                        }}
                      />
                    ))}
                  </Stack>
                </Box>
              )}

              {profile.websiteUrl && (
                <Box className="community-profile__sidebar-section community-profile__sidebar-section--website">
                  <Typography className="community-profile__sidebar-label" variant="caption" sx={{ fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase' }}>
                    Website
                  </Typography>
                  <Typography className="community-profile__sidebar-value" variant="body1" sx={{ fontWeight: 700, mt: 0.75 }}>
                    <Link href={profile.websiteUrl} target="_blank" rel="noreferrer" underline="hover">
                      Visit website
                    </Link>
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Container>

      <Box className="community-profile__footer site-footer" sx={{ pt: 10, pb: 4, bgcolor: '#000430', color: '#ffffff' }}>
        <Container className="site-footer__links" maxWidth="lg" sx={{ mt: 8 }}>
          <Grid className="site-footer__grid" container spacing={6}>
            <Grid className="site-footer__brand" item xs={12} md={4}>
              <Box
                component="img"
                className="site-footer__logo"
                src={`${process.env.PUBLIC_URL}/sglogo.svg`}
                alt="SuperGauge"
                sx={{ height: 32, width: 'auto', display: 'block', mb: 1.5 }}
              />
              <Typography
                className="site-footer__branddesc"
                variant="body2"
                sx={{ mt: 1.5, color: 'rgba(255,255,255,0.8)' }}
              >
                Gather the insights you need to make people-driven, data-informed decisions about your company culture.
              </Typography>
            </Grid>
            <Grid className="site-footer__col" item xs={12} md={2} />
            <Grid className="site-footer__col" item xs={12} md={2} />
            <Grid className="site-footer__col" item xs={12} md={2} />
          </Grid>
        </Container>

        <Container className="site-footer__bar" maxWidth="lg" sx={{ mt: 6 }}>
          <Box className="site-footer__bar-inner" sx={{ borderTop: '1px solid', borderColor: 'rgba(255,255,255,0.2)', pt: 2 }}>
            <Grid className="site-footer__bar-grid" container alignItems="center">
              <Grid className="site-footer__bar-left" item xs={12} md={6}>
                <Typography className="site-footer__copyright" variant="caption">
                  © {new Date().getFullYear()} SuperGauge
                </Typography>
              </Grid>
              <Grid className="site-footer__bar-right" item xs={12} md={6}>
                <Box className="site-footer__contact" sx={{ textAlign: { xs: 'left', md: 'right' } }}>
                  <Typography className="site-footer__link" variant="caption">Contact</Typography>
                  <Typography className="site-footer__link" variant="caption">Terms</Typography>
                  <Typography className="site-footer__link" variant="caption">Privacy policy</Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default CommunityDetail;
